import type { FC, ReactElement } from 'react';
import { useEffect } from 'react';

import { CrossOriginCommunication } from '../../../controllers/crossOriginCommunication/CrossOriginCommunicationController';
import { OutgoingMessage } from '../../../controllers/crossOriginCommunication/types';
import { useRedirectToLobbyCallbackConnected } from '../../../hooks/useRedirectToLobbyCallbackConnected';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { configActions } from '../../../store/slices/config/slice';
import { useHeaderSlice } from '../../../store/slices/header/selectors';
import { headerActions } from '../../../store/slices/header/slice';
import { HeaderModal } from '../../../store/slices/header/types';
import { usePopupSlice } from '../../../store/slices/popup/selectors';
import { popupActions } from '../../../store/slices/popup/slice';
import { Button, ButtonVariant } from '../../controls/button/Button';
import { NavPages } from '../../nav/navPages/NavPagesConnected';
import { PopupType } from '../../popup/types';
import { Popup } from '../Popup';

import { BlockingModalContent } from './BlockingModalContent';

/**
 * In most scenarios, for UK jurisdiction, server sends a custom RC message with untranslated content which is handled as a generic Popup.
 * This variation of RC component uses localised button labels with dynamic interval and session time values.
 *
 * Refer to https://pragmaticplay.atlassian.net/browse/UIC-505 to trigger this variation on UI using EMUL.
 */
export const RealityCheckConnected: FC = (): ReactElement | null => {
  const dispatch = useCoreDispatch();

  const baseUrl = useConfigSlice.baseUrl();
  const realityCheck = useConfigSlice.realityCheck();
  const JSESSIONID = useConfigSlice.session()?.JSESSIONID;
  const tableId = useConfigSlice.session()?.tableId as string;
  const redirectToLobby = useRedirectToLobbyCallbackConnected();
  const currentPopup = usePopupSlice.currentPopup();
  const openedModal = useHeaderSlice.getCoreOpenedModal();
  const homeUrl = useConfigSlice.casinoConfig()?.homeUrl;
  const operatorGameId = useConfigSlice.tableConfig()?.operatorGameId;

  useEffect(() => {
    CrossOriginCommunication.dispatch(OutgoingMessage.RealityCheckShown, {
      tableId: operatorGameId,
    });
  }, [operatorGameId]);

  const url = `${baseUrl}/api/cgibin/regulation/realityCheck?JSESSIONID=${JSESSIONID}`;

  const action = [
    <Button
      key="CONTINUE_PLAYING"
      tk="CONTINUE_PLAYING"
      variant={ButtonVariant.ACCENT}
      onClick={() => {
        // Do not wait for response
        void fetch(url, {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({ userAction: 'RESUME', tableId: tableId }),
        });
        CrossOriginCommunication.dispatch(OutgoingMessage.RealityCheckContinue, {
          tableId: operatorGameId,
        });
        dispatch(configActions.setRealityCheck(null));
        dispatch(popupActions.dismissPopup(PopupType.RealityCheck));
      }}
    />,
    <Button
      key="HISTORY"
      tk="HISTORY"
      variant={ButtonVariant.MAIN}
      onClick={() => {
        // Do not wait for response
        if (openedModal === HeaderModal.History) {
          dispatch(headerActions.setOpenedModal(null));
        } else {
          dispatch(headerActions.setOpenedModal(HeaderModal.History));
        }
        dispatch(headerActions.setNavRoute([NavPages.HOME, NavPages.HISTORY]));
      }}
    />,
    <Button
      key="STOP"
      tk="STOP"
      variant={ButtonVariant.MAIN}
      onClick={() => {
        CrossOriginCommunication.dispatch(OutgoingMessage.RealityCheckQuit, {
          tableId: operatorGameId,
        });
        fetch(url, {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({ userAction: 'STOP', tableId: tableId }),
        }).then(() => {
          if (homeUrl && window.top) {
            window.top.location.href = homeUrl;
          } else {
            redirectToLobby();
          }
        });
      }}
    />,
  ];

  if (currentPopup === PopupType.RealityCheck) {
    const component = (
      <L10nText tk="REALITY_CHECK_MESSAGE" dynamicValues={[realityCheck!.interval, realityCheck!.passed]} />
    );

    const content = (
      <BlockingModalContent title={<L10nText tk="GAME_CARE" />} actions={action}>
        {component}
      </BlockingModalContent>
    );

    return (
      <div style={{ zIndex: 10, position: 'absolute' }}>
        <Popup content={content} headerLess={true} handleClose={() => null} />
      </div>
    );
  }

  return null;
};
